import BlogHeader from '@/components/BlogHeader'
import Main from '@/components/Main'
import PostFeed from '@/components/PostFeed'
import SearchForm from '@/components/SearchForm'
import SEO from '@/components/SEO'
import { FeaturedImageFragment } from '@/fragments/FeaturedImage'
import styles from '@/styles/pages/_default.module.scss'
import { formatCategoryName } from '@/utilities/contentHelpers'
import { gql, useApolloClient } from '@apollo/client'
import { useRouter } from 'next/router'
import { startTransition, useCallback, useEffect, useRef, useState } from 'react'

const POSTS_PER_PAGE = 12

const BLOG_POSTS_QUERY = gql`
  ${FeaturedImageFragment}
  query GetBlogPosts($first: Int!, $after: String, $search: String, $categoryName: String) {
    posts(first: $first, after: $after, where: { search: $search, categoryName: $categoryName }) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          date
          uri
          title
          excerpt
          categories {
            nodes {
              name
            }
          }
          ...FeaturedImageFragment
          postFields {
            blogSliderImage {
              node {
                sourceUrl
                altText
              }
            }
          }
        }
      }
    }
  }
`

function safeSessionGet(key) {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSessionSet(key, value) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* storage unavailable (e.g. Safari private mode) */
  }
}

function safeSessionRemove(key) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* storage unavailable */
  }
}

const EMPTY_PAGE_INFO = { hasNextPage: false, endCursor: null, queryKey: '||' }

function fetchBlogPosts(client, { after, search, categoryName }) {
  return client
    .query({
      query: BLOG_POSTS_QUERY,
      variables: {
        first: POSTS_PER_PAGE,
        after,
        search: search || undefined,
        categoryName: categoryName || undefined
      },
      fetchPolicy: 'network-only'
    })
    .then((result) => result.data?.posts)
}

export default function BlogPage(props) {
  const router = useRouter()
  const client = useApolloClient()
  const [currentSearchTerm, setCurrentSearchTerm] = useState('')
  const [currentCategory, setCurrentCategory] = useState('')
  const debounceRef = useRef(null)
  const ssrPosts = props?.data?.posts?.nodes ?? []
  const ssrPageInfo = {
    ...(props?.data?.posts?.pageInfo ?? EMPTY_PAGE_INFO),
    queryKey: '||'
  }
  const [loadedPosts, setLoadedPosts] = useState(ssrPosts)
  const [displayCount, setDisplayCount] = useState(POSTS_PER_PAGE)
  const [pageInfo, setPageInfo] = useState(ssrPageInfo)
  const browseRef = useRef({ posts: ssrPosts, displayCount: POSTS_PER_PAGE, pageInfo: ssrPageInfo })
  const requestRef = useRef(0)
  const skipBrowseRestore = useRef(true)
  const queryKey = `${currentSearchTerm}||${currentCategory}`

  useEffect(() => {
    const savedTerm = safeSessionGet('searchTerm') || ''
    const savedCategory = safeSessionGet('searchCategory') || ''
    if (savedTerm || savedCategory) {
      startTransition(() => {
        setCurrentSearchTerm(savedTerm)
        setCurrentCategory(savedCategory)
      })
    }
  }, [])

  useEffect(() => {
    const savedScrollY = parseInt(safeSessionGet('scrollY') || '0', 10)
    if (savedScrollY <= 0) return

    const timer = setTimeout(() => {
      window.scrollTo(0, savedScrollY)
      safeSessionRemove('scrollY')
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (currentSearchTerm || currentCategory || pageInfo.queryKey !== '||') return
    browseRef.current = { posts: loadedPosts, displayCount, pageInfo }
  }, [loadedPosts, displayCount, pageInfo, currentSearchTerm, currentCategory])

  useEffect(() => {
    const requestId = ++requestRef.current

    if (!currentSearchTerm && !currentCategory) {
      if (skipBrowseRestore.current) {
        skipBrowseRestore.current = false
        return
      }

      const saved = browseRef.current
      setLoadedPosts(saved.posts)
      setDisplayCount(saved.displayCount)
      setPageInfo({ ...saved.pageInfo, queryKey: '||' })
      return
    }

    skipBrowseRestore.current = false
    setDisplayCount(POSTS_PER_PAGE)
    setPageInfo({ hasNextPage: false, endCursor: null, queryKey })

    fetchBlogPosts(client, { search: currentSearchTerm, categoryName: currentCategory }).then((connection) => {
      if (requestId !== requestRef.current) return
      setLoadedPosts(connection?.edges?.map((edge) => edge.node) ?? [])
      setPageInfo({ ...(connection?.pageInfo ?? EMPTY_PAGE_INFO), queryKey })
    })
  }, [currentSearchTerm, currentCategory, queryKey, client])

  useEffect(() => {
    if (pageInfo.queryKey !== queryKey) return
    if (loadedPosts.length > displayCount) return
    if (!pageInfo.hasNextPage || !pageInfo.endCursor) return

    const requestId = requestRef.current
    let cancelled = false

    fetchBlogPosts(client, {
      after: pageInfo.endCursor,
      search: currentSearchTerm,
      categoryName: currentCategory
    }).then((connection) => {
      if (cancelled || requestId !== requestRef.current || !connection) return
      const incoming = connection.edges?.map((edge) => edge.node) ?? []
      setLoadedPosts((prev) => {
        const seen = new Set(prev.map((post) => post.id))
        const next = incoming.filter((post) => post?.id && !seen.has(post.id))
        return next.length ? [...prev, ...next] : prev
      })
      if (connection.pageInfo) {
        setPageInfo({ ...connection.pageInfo, queryKey })
      }
    })

    return () => {
      cancelled = true
    }
  }, [
    loadedPosts.length,
    displayCount,
    pageInfo.endCursor,
    pageInfo.hasNextPage,
    pageInfo.queryKey,
    queryKey,
    currentSearchTerm,
    currentCategory,
    client
  ])

  useEffect(() => {
    const saveScrollPosition = () => {
      safeSessionSet('scrollY', window.scrollY.toString())
    }

    const handleRouteChangeStart = () => saveScrollPosition()

    router.events.on('routeChangeStart', handleRouteChangeStart)
    window.addEventListener('beforeunload', saveScrollPosition)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      window.removeEventListener('beforeunload', saveScrollPosition)
    }
  }, [router.events])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleSearch = useCallback((searchTerm, selectedCategory) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      const resolvedCategory = selectedCategory && formatCategoryName(selectedCategory).toLowerCase() !== 'view all' ? selectedCategory : ''

      safeSessionSet('searchTerm', searchTerm)
      safeSessionSet('searchCategory', resolvedCategory)
      setCurrentSearchTerm(searchTerm)
      setCurrentCategory(resolvedCategory)
      safeSessionSet('scrollY', '0')
    }, 300)
  }, [])

  const handleViewMore = useCallback(() => {
    setDisplayCount((count) => count + POSTS_PER_PAGE)
  }, [])

  if (props.loading) {
    return null
  }

  const metaDesc = props?.data?.page?.seo?.metaDesc
  const { title, featuredImage } = props?.data?.page ?? { title: '' }
  const categories = props?.data?.categories
  const seoTitle = props?.data?.page?.seo?.title

  return (
    <>
      <SEO title={seoTitle || title} description={metaDesc} imageUrl={featuredImage?.node?.sourceUrl} />

      <BlogHeader pageFields={props?.data?.page} image={featuredImage?.node} disableOverlay={true} />

      <Main>
        <div className={`container ${styles.workContainer}`}>
          <SearchForm
            categories={categories}
            onSearch={handleSearch}
            initialSearchTerm={currentSearchTerm}
            initialCategory={currentCategory}
          />
          <div className={styles.blogFeed}>
            <section className={styles.posts}>
              <PostFeed
                posts={loadedPosts.slice(0, displayCount)}
                id="posts-list"
                onViewMore={handleViewMore}
                canViewMore={displayCount < loadedPosts.length || !!pageInfo.hasNextPage}
              />
            </section>
          </div>
        </div>
      </Main>
    </>
  )
}
